import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog, { useSheetLayout } from '../common/Dialog';
import { useAuth } from '../../contexts/AuthContext';
import CardNewsBody from './CardNewsBody';
import { isPrerendering } from './announcementStorage';
import { setWhatsNewOpen, wasNoticePopupShown } from './popupGate';
import {
  WHATS_NEW_OPEN_EVENT,
  getUnseenWhatsNew,
  getWhatsNewItems,
  isWhatsNewBlockedPath,
  markWhatsNewSeen,
} from '../../config/whatsNew';
import { trackEvent } from '../../utils/eventTracker';
import './AnnouncementModal.css';

export const WHATS_NEW_OPEN_DELAY_MS = 2500;
const UPDATES_PATH = '/help?tab=updates';

/**
 * 새 소식 — 고객센터 업데이트를 카드뉴스로 보여 주는 팝업 (규칙은 config/whatsNew.js).
 *
 * - 자동: 로그인 사용자가 아직 못 본 소식이 있고, 이번 방문에 공지 팝업이 뜨지 않았으면 잠시 뒤 한 번 연다.
 * - 메뉴: openWhatsNew()로 최근 소식을 다시 연다. 보여 줄 소식이 없으면 고객센터 업데이트 목록으로 간다.
 * - 열리는 순간 본 것으로 기록한다. 닫기·확인 모두 같은 뜻이다.
 */
const WhatsNewModal = ({ pathname = '' }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || '';
  const accountCreatedAt = currentUser?.metadata?.creationTime;
  const [view, setView] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const isOpen = Boolean(view);
  const isSheet = useSheetLayout(isOpen);
  const isOpenRef = useRef(false);
  const autoShownRef = useRef(false);
  const tabRefs = useRef([]);

  const track = useCallback((action, items, entry, extra = {}) => {
    trackEvent(`whats_new_${action}`, {
      entry,
      update_ids: items.map(item => item.id).join(','),
      count: items.length,
      ...extra,
    });
  }, []);

  const open = useCallback((items, entry) => {
    isOpenRef.current = true;
    setWhatsNewOpen(true);
    setActiveIndex(0);
    setView({ items, entry });
    markWhatsNewSeen(uid, items.map(item => item.id));
    track('open', items, entry);
  }, [track, uid]);

  const close = useCallback(() => {
    isOpenRef.current = false;
    setWhatsNewOpen(false);
    setView(null);
  }, []);

  useEffect(() => () => setWhatsNewOpen(false), []);

  // 채널톡 버튼이 바텀시트 버튼을 가리지 않게 한다 (공지 팝업과 같은 규칙).
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;
    document.body.classList.add('announcement-modal-open');
    return () => document.body.classList.remove('announcement-modal-open');
  }, [isOpen]);

  useEffect(() => {
    if (!uid || autoShownRef.current || isPrerendering() || isWhatsNewBlockedPath(pathname)) return undefined;
    const timer = setTimeout(() => {
      if (isOpenRef.current || wasNoticePopupShown()) return;
      const items = getUnseenWhatsNew({ uid, accountCreatedAt });
      if (items.length === 0) return;
      autoShownRef.current = true;
      open(items, 'auto');
    }, WHATS_NEW_OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [accountCreatedAt, open, pathname, uid]);

  // 탭이 많으면 가로로 넘치므로, 고른 탭(다음 소식 포함)이 늘 보이게 한다.
  useEffect(() => {
    if (!isOpen) return;
    tabRefs.current[activeIndex]?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [activeIndex, isOpen]);

  useEffect(() => {
    const handleOpen = () => {
      if (isOpenRef.current) return;
      const items = getWhatsNewItems();
      if (items.length === 0) {
        navigate(UPDATES_PATH);
        return;
      }
      open(items, 'menu');
    };
    window.addEventListener(WHATS_NEW_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(WHATS_NEW_OPEN_EVENT, handleOpen);
  }, [navigate, open]);

  if (!view) return null;

  const { items, entry } = view;
  const activeItem = items[activeIndex];
  const hasMultiple = items.length > 1;
  const isLast = activeIndex === items.length - 1;

  const handleClose = () => {
    track('dismiss', items, entry, { update_id: activeItem.id });
    close();
  };

  const goTo = (path, action) => {
    track(action, items, entry, { update_id: activeItem.id });
    close();
    navigate(path);
  };

  const handleTabKeyDown = (event) => {
    const lastIndex = items.length - 1;
    const keyTargets = {
      ArrowRight: activeIndex === lastIndex ? 0 : activeIndex + 1,
      ArrowLeft: activeIndex === 0 ? lastIndex : activeIndex - 1,
      Home: 0,
      End: lastIndex,
    };
    if (!(event.key in keyTargets)) return;
    event.preventDefault();
    setActiveIndex(keyTargets[event.key]);
    tabRefs.current[keyTargets[event.key]]?.focus();
  };

  const panelClassName = [
    'announcement-modal',
    isSheet && 'announcement-modal--sheet',
    hasMultiple && 'announcement-modal--multi',
  ].filter(Boolean).join(' ');

  const footer = (
    <div className="announcement-modal__actions" data-testid="whats-new-actions">
      <button
        className="announcement-modal__primary"
        onClick={() => goTo(activeItem.detailPath, 'click')}
        type="button"
      >
        자세히 보기
      </button>
      <div className="announcement-modal__subactions">
        <button
          className="announcement-modal__text-button"
          onClick={() => goTo(UPDATES_PATH, 'all')}
          type="button"
        >
          지난 소식 보기
        </button>
        {hasMultiple && !isLast && (
          <button
            className="announcement-modal__text-button announcement-modal__text-button--next"
            onClick={() => setActiveIndex(activeIndex + 1)}
            type="button"
          >
            다음 소식
            <svg aria-hidden="true" fill="none" height="16" viewBox="0 0 24 24" width="16">
              <path d="M9 5l7 7-7 7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );

  return (
    <Dialog
      closeLabel="닫기"
      dragHandle
      footer={footer}
      hideTitle
      onClose={handleClose}
      open={isOpen}
      panelClassName={panelClassName}
      size="sm"
      testId="whats-new-modal"
      title={`새 소식: ${activeItem.title}`}
    >
      {hasMultiple && (
        <div aria-label="새 소식 목록" className="announcement-modal__tabs" role="tablist">
          {items.map((item, index) => (
            <button
              aria-controls={`whats-new-panel-${item.id}`}
              aria-selected={index === activeIndex}
              className="announcement-modal__tab"
              id={`whats-new-tab-${item.id}`}
              key={item.id}
              onClick={() => setActiveIndex(index)}
              onKeyDown={handleTabKeyDown}
              ref={(node) => { tabRefs.current[index] = node; }}
              role="tab"
              tabIndex={index === activeIndex ? 0 : -1}
              type="button"
            >
              {item.tabLabel}
            </button>
          ))}
        </div>
      )}
      <div
        aria-labelledby={hasMultiple ? `whats-new-tab-${activeItem.id}` : undefined}
        className="announcement-modal__page"
        id={`whats-new-panel-${activeItem.id}`}
        key={activeItem.id}
        role={hasMultiple ? 'tabpanel' : undefined}
      >
        <CardNewsBody announcement={activeItem} showArrows={!isSheet} />
      </div>
    </Dialog>
  );
};

export default WhatsNewModal;
